import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Mail } from "lucide-react";
import { useLocalization } from "../hooks/useLocalization";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const { t } = useLocalization();

  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerificationStatus("error");
      setMessage(t('noVerificationToken'));
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/verify-email`, { token });
      setVerificationStatus("success");
      setMessage(response.data.message);
    } catch (err) {
      setVerificationStatus("error");
      setMessage(err.response?.data?.detail || t('emailVerificationError'));
    }
  };

  const handleResendEmail = async () => {
    const email = prompt(t('enterEmailToResend'));
    if (!email) return;

    setIsResending(true);
    try {
      await axios.post(`${API}/resend-verification`, { email });
      alert(t('verificationEmailSent'));
    } catch (err) {
      alert(t('errorSendingEmail'));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto p-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {t('emailVerification')}
            </h1>
            <p className="text-gray-600">
              {t('title')}
            </p>
          </div>

          {/* Loading State */}
          {verificationStatus === "loading" && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('verifyingEmail')}</p>
            </div>
          )}

          {/* Success State */}
          {verificationStatus === "success" && (
            <div className="text-center">
              <div className="mb-6">
                <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-green-600 mb-2">
                  {t('emailVerified')}
                </h2>
                <p className="text-gray-600 mb-6">
                  {message}
                </p>
              </div>
              
              <div className="space-y-4">
                <button
                  onClick={() => navigate("/user-login")}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors duration-300 font-medium"
                >
                  {t('login')}
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 font-medium flex items-center justify-center"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  {t('backToHome')}
                </button>
              </div>
            </div>
          )}

          {/* Error State */}
          {verificationStatus === "error" && (
            <div className="text-center">
              <div className="mb-6">
                <XCircle size={64} className="text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-600 mb-2">
                  {t('verificationError')}
                </h2>
                <p className="text-gray-600 mb-6">
                  {message}
                </p>
              </div>
              
              <div className="space-y-4">
                <button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 font-medium flex items-center justify-center"
                >
                  {isResending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('sending')}
                    </>
                  ) : (
                    <>
                      <Mail size={20} className="mr-2" />
                      {t('resendVerificationEmail')}
                    </>
                  )}
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300 font-medium flex items-center justify-center"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  {t('backToHome')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;